import { CanMatchFn, Router, UrlSegment, Route } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { TokenStorageService } from '../services/token-storage.service';
import { InitialSetupService } from '../services/initial-setup.service';
import { loadUser } from '@/app/dashboard/store/dashboard.actions';
import { ToastService } from '@app/shared/components/toast/services/toast.service';

function hasValidSession(): boolean {
  const tokenStorage = inject(TokenStorageService);
  return Boolean(
    tokenStorage.getAccessToken() ?? tokenStorage.getRefreshToken()
  );
}

export const authenticatedGuard: CanMatchFn = (
  _route: Route,
  _segments: UrlSegment[]
) => {
  if (!hasValidSession()) {
    return inject(Router).createUrlTree(['/auth']);
  }

  // Child-route `permissionGuard` waits on the profile before matching.
  // On a hard refresh that guard runs before `LayoutComponent` boots, so
  // kick off the profile fetch here instead of only in the shell.
  inject(Store).dispatch(loadUser());

  return true;
};

export const redirectIfAuthenticatedGuard: CanMatchFn = (
  _route: Route,
  _segments: UrlSegment[]
) => {
  if (hasValidSession()) {
    return inject(Router).createUrlTree(['/dashboard/account-settings']);
  }
  const router = inject(Router);
  const isInitialSetupPage = _segments.some(s => s.path === 'initial-setup');
  if (isInitialSetupPage) {
    return true;
  }

  // When the platform cannot say whether it still needs setting up, the
  // visitor goes on to sign in — a configured platform must stay reachable
  // through a status blip, and the backend refuses the wizard once any account
  // exists — but never silently (F-020, user decision). A failed request is
  // already toasted by `errorInterceptor` and is not cached; an unreadable
  // 200 is toasted here and dropped from the cache so the next navigation
  // asks again.
  const initialSetup = inject(InitialSetupService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);
  return initialSetup.getStatus().pipe(
    map(res => {
      const required = res?.data?.initialSetupRequired;
      if (typeof required !== 'boolean') {
        initialSetup.invalidateStatus();
        toast.error(
          translate.instant('auth.initialSetup.errors.statusUnreadable')
        );
        return true;
      }
      return required ? router.createUrlTree(['/auth/initial-setup']) : true;
    }),
    catchError(() => of(true))
  );
};
